import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { DollarSign, Calendar, Clock, User } from "lucide-react";

const CreateInvoiceDialog = ({ onCreateInvoice, consultants = [], clientData = {} }) => {
  const [open, setOpen] = useState(false);
  const [invoice, setInvoice] = useState({
    amount: "",
    description: "",
    due_date: "",
    consultant_id: "",
    hours_worked: "",
    period_start: "",
    period_end: "",
    line_items: [],
  });

  // Get hourly rate from client data (default to 150 if not set)
  const hourlyRate = clientData?.hourly_rate || 150;

  // Set default consultant to assigned consultant if available
  useEffect(() => {
    if (clientData?.assigned_consultant_id) {
      setInvoice(prev => ({
        ...prev,
        consultant_id: clientData.assigned_consultant_id
      }));
    }
  }, [clientData]);

  // Calculate amount based on hours when hours change
  const handleHoursChange = (hours) => {
    const calculatedAmount = hours ? (parseFloat(hours) * hourlyRate).toFixed(2) : "";
    setInvoice(prev => ({
      ...prev,
      hours_worked: hours,
      amount: calculatedAmount
    }));
  };

  const handleSubmit = async () => {
    if (!invoice.amount || !invoice.description || !invoice.due_date) {
      return;
    }
    
    const success = await onCreateInvoice({
      ...invoice,
      amount: parseFloat(invoice.amount),
      hours_worked: invoice.hours_worked ? parseFloat(invoice.hours_worked) : null,
    });
    
    if (success) {
      setOpen(false);
      setInvoice({
        amount: "",
        description: "",
        due_date: "",
        consultant_id: clientData?.assigned_consultant_id || "",
        hours_worked: "",
        period_start: "",
        period_end: "",
        line_items: [],
      });
    }
  };

  // Get today's date for default values
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Card className="cursor-pointer hover:bg-slate-50 transition-colors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-rnb-teal/10 rounded-lg flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-rnb-teal" />
            </div>
            <div>
              <p className="font-medium text-rnb-navy">Invoice</p>
              <p className="text-xs text-rnb-gray">Create new</p>
            </div>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
          <DialogDescription>
            Create a new invoice for this client. Fill in the billing details below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
          {/* Assigned Consultant */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Consultant
            </Label>
            <Select
              value={invoice.consultant_id}
              onValueChange={(value) => setInvoice({ ...invoice, consultant_id: value })}
            >
              <SelectTrigger data-testid="invoice-consultant">
                <SelectValue placeholder="Select consultant" />
              </SelectTrigger>
              <SelectContent>
                {consultants.map((consultant) => (
                  <SelectItem key={consultant.user_id} value={consultant.user_id}>
                    {consultant.name || consultant.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Billing Period */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Period Start
              </Label>
              <Input
                type="date"
                value={invoice.period_start}
                onChange={(e) => setInvoice({ ...invoice, period_start: e.target.value })}
                data-testid="invoice-period-start"
              />
            </div>
            <div className="space-y-2">
              <Label>Period End</Label>
              <Input
                type="date"
                value={invoice.period_end}
                onChange={(e) => setInvoice({ ...invoice, period_end: e.target.value })}
                data-testid="invoice-period-end"
              />
            </div>
          </div>

          {/* Hours Worked */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Hours Worked
            </Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              placeholder="e.g., 10.5"
              value={invoice.hours_worked}
              onChange={(e) => handleHoursChange(e.target.value)}
              data-testid="invoice-hours"
            />
            <p className="text-xs text-rnb-gray">
              Amount will be calculated at ${hourlyRate}/hour
            </p>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Amount *
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={invoice.amount}
              onChange={(e) => setInvoice({ ...invoice, amount: e.target.value })}
              data-testid="invoice-amount"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              placeholder="Describe the services rendered..."
              value={invoice.description}
              onChange={(e) => setInvoice({ ...invoice, description: e.target.value })}
              rows={3}
              data-testid="invoice-description"
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date *</Label>
            <Input
              type="date"
              value={invoice.due_date || thirtyDaysFromNow}
              onChange={(e) => setInvoice({ ...invoice, due_date: e.target.value })}
              data-testid="invoice-due-date"
            />
          </div>

          <Button 
            onClick={handleSubmit} 
            className="w-full" 
            data-testid="submit-invoice"
            disabled={!invoice.amount || !invoice.description || !invoice.due_date}
          >
            Create Invoice
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvoiceDialog;
